import React, { useState, useEffect } from 'react';
import { Search, CheckCircle, Clock, CreditCard, Eye, X } from 'lucide-react';
import { admissionsService } from '../../firebase/admissions';
import toast from 'react-hot-toast';

const Refunds = () => {
  const [refunds, setRefunds] = useState([]);
  const [filteredRefunds, setFilteredRefunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRefund, setSelectedRefund] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    loadRefunds();
  }, []);

  useEffect(() => {
    filterRefunds();
  }, [searchTerm, refunds]);

  const loadRefunds = async () => {
    try {
      setLoading(true);
      const result = await admissionsService.getAllRefunds();
      if (result.success) {
        setRefunds(result.refunds || []);
      } else {
        toast.error('Failed to load refunds: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      toast.error('Error loading refunds: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const filterRefunds = () => {
    if (!searchTerm.trim()) {
      setFilteredRefunds(refunds);
      return;
    }

    const term = searchTerm.toLowerCase().trim();
    const filtered = refunds.filter(refund =>
      refund.studentName?.toLowerCase().includes(term) ||
      refund.studentId?.toLowerCase().includes(term) ||
      refund.institutionName?.toLowerCase().includes(term) ||
      refund.institutionId?.toLowerCase().includes(term) ||
      refund.transactionId?.toLowerCase().includes(term) ||
      refund.bankAccountDetails?.accountNumber?.toLowerCase().includes(term) ||
      refund.bankAccountDetails?.ifscCode?.toLowerCase().includes(term)
    );

    setFilteredRefunds(filtered);
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { icon: Clock, color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200', label: 'Pending' },
      refunded: { icon: CheckCircle, color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200', label: 'Refunded' },
      processing: { icon: Clock, color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200', label: 'Processing' }
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    return (
      <span className={`${badge.color} px-3 py-1 rounded-full text-sm flex items-center gap-1`}>
        <Icon className="w-4 h-4" />
        {badge.label}
      </span>
    );
  };

  const handleUpdateStatus = async (refundId, newStatus) => {
    if (!window.confirm(`Are you sure you want to mark this refund as ${newStatus}?`)) {
      return;
    }

    try {
      setUpdatingStatus(true);
      const result = await admissionsService.updateRefundStatus(refundId, newStatus);
      if (result.success) {
        toast.success(`Refund status updated to ${newStatus}`);
        loadRefunds();
        if (showDetails) {
          setShowDetails(false);
          setSelectedRefund(null);
        }
      } else {
        toast.error('Failed to update refund status: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      toast.error('Error updating refund status: ' + error.message);
    } finally {
      setUpdatingStatus(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Refunds Management</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          View and manage refund requests for rejected applications
        </p>
      </div>

      {/* Search Bar */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by student name, ID, institution, transaction ID, account number, or IFSC code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
        </div>
      </div>

      {/* Refunds Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
        {filteredRefunds.length === 0 ? (
          <div className="p-12 text-center">
            <CreditCard className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400 text-lg mb-2">No refunds found</p>
            <p className="text-gray-500 dark:text-gray-500 text-sm">
              {searchTerm ? 'Try adjusting your search terms' : 'No refund requests yet'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Student</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Institution</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Transaction</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredRefunds.map((refund) => (
                  <tr key={refund.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {refund.studentName || 'N/A'}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        ID: {refund.studentId?.substring(0, 8)}...
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {refund.institutionName || 'N/A'}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        ID: {refund.institutionId?.substring(0, 8)}...
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-xs text-gray-600 dark:text-gray-400 font-mono">
                        {refund.transactionId?.substring(0, 20)}...
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      ₹{refund.transactionAmount || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(refund.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      {refund.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => {
                          setSelectedRefund(refund);
                          setShowDetails(true);
                        }}
                        className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                        title="View Details"
                      >
                        <Eye className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Refund Details Modal */}
      {showDetails && selectedRefund && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Refund Details</h2>
                <button
                  onClick={() => {
                    setShowDetails(false);
                    setSelectedRefund(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Student Information */}
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Student Information</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Name:</span>
                      <span className="ml-2 text-gray-900 dark:text-white font-medium">
                        {selectedRefund.studentName || 'N/A'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Student ID:</span>
                      <span className="ml-2 text-gray-900 dark:text-white font-mono text-xs">
                        {selectedRefund.studentId || 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Institution Information */}
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Institution Information</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Name:</span>
                      <span className="ml-2 text-gray-900 dark:text-white font-medium">
                        {selectedRefund.institutionName || 'N/A'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Institution ID:</span>
                      <span className="ml-2 text-gray-900 dark:text-white font-mono text-xs">
                        {selectedRefund.institutionId || 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Transaction Information */}
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Transaction Information</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Transaction ID:</span>
                      <span className="ml-2 text-gray-900 dark:text-white font-mono text-xs">
                        {selectedRefund.transactionId || 'N/A'}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400">Amount:</span>
                      <span className="ml-2 text-gray-900 dark:text-white font-medium">
                        ₹{selectedRefund.transactionAmount || 0}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Bank Account Details */}
                {selectedRefund.bankAccountDetails && (
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Bank Account Details</h3>
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-2 text-sm">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <span className="text-gray-600 dark:text-gray-400">Account Holder Name:</span>
                          <span className="ml-2 text-gray-900 dark:text-white font-medium">
                            {selectedRefund.bankAccountDetails.accountHolderName || 'N/A'}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600 dark:text-gray-400">Account Number:</span>
                          <span className="ml-2 text-gray-900 dark:text-white font-mono">
                            {selectedRefund.bankAccountDetails.accountNumber || 'N/A'}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600 dark:text-gray-400">IFSC Code:</span>
                          <span className="ml-2 text-gray-900 dark:text-white font-mono">
                            {selectedRefund.bankAccountDetails.ifscCode || 'N/A'}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600 dark:text-gray-400">Bank Name:</span>
                          <span className="ml-2 text-gray-900 dark:text-white">
                            {selectedRefund.bankAccountDetails.bankName || 'N/A'}
                          </span>
                        </div>
                        {selectedRefund.bankAccountDetails.branchName && (
                          <div>
                            <span className="text-gray-600 dark:text-gray-400">Branch Name:</span>
                            <span className="ml-2 text-gray-900 dark:text-white">
                              {selectedRefund.bankAccountDetails.branchName}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Status */}
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Refund Status</h3>
                  <div className="flex items-center gap-3">
                    {getStatusBadge(selectedRefund.status)}
                    {selectedRefund.status === 'pending' && (
                      <button
                        onClick={() => handleUpdateStatus(selectedRefund.id, 'refunded')}
                        disabled={updatingStatus}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 text-sm disabled:opacity-50"
                      >
                        <CheckCircle className="w-4 h-4" />
                        {updatingStatus ? 'Updating...' : 'Mark as Refunded'}
                      </button>
                    )}
                  </div>
                </div>

                {/* Date Information */}
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Date Information</h3>
                  <div className="text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Request Date:</span>
                    <span className="ml-2 text-gray-900 dark:text-white">
                      {selectedRefund.createdAt?.toDate?.()?.toLocaleString() || 'N/A'}
                    </span>
                  </div>
                  {selectedRefund.updatedAt && (
                    <div className="text-sm mt-1">
                      <span className="text-gray-600 dark:text-gray-400">Last Updated:</span>
                      <span className="ml-2 text-gray-900 dark:text-white">
                        {selectedRefund.updatedAt?.toDate?.()?.toLocaleString() || 'N/A'}
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button
                    onClick={() => {
                      setShowDetails(false);
                      setSelectedRefund(null);
                    }}
                    className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Refunds;

